/**
 * 소셜 로그인 — **제공자 SDK 를 만지는 유일한 파일** (Google · Naver).
 *
 * 여기서 하는 일은 "제공자에게서 자격증명을 받아 오는 것" 까지다. 그것을 QBase 에 넘겨
 * 세션을 만드는 일은 `qbaseClient` 가 하고, 앱 상태로 옮기는 일은 `authActions` 가 한다.
 * 세 겹을 섞지 않는 이유: 두 제공자가 **받아 오는 모양이 다르다**(Google 은 팝업에서
 * access_token, Naver 는 리다이렉트 뒤 code+state) — 그 차이를 여기서 흡수해야 위쪽 두
 * 층이 제공자를 몰라도 된다.
 *
 * ## 라이브러리를 쓰지 않는 이유
 *
 * 오픈데이터모델러는 `@react-oauth/google` 을 쓴다. 이 저장소는 설치가 막힌다 —
 * typescript 7 과 react-scripts 5 의 peer 가 이미 충돌해 있어서 새 패키지를 넣으려면
 * `--legacy-peer-deps` 로 트리를 흔들어야 한다. Google Identity Services 를 직접 부르면
 * 30줄이고, **Naver 쪽과 같은 모양**(스크립트 하나 · 상태 하나)이 된다.
 *
 * ## 설정이 없으면 **조용히 되지 않게** 한다
 *
 * 클라이언트 ID 가 없으면 버튼을 감추고 이유를 말한다. QBase 의 `qbaseSysId` 처럼 조용한
 * 폴백이 있는 값이 아니라서 앱을 죽일 이유는 없지만, "버튼은 있는데 눌러도 아무 일이
 * 없는" 상태는 만들지 않는다.
 */

/** 콘솔에서 발급한 값. 없으면 그 제공자의 버튼이 뜨지 않는다. */
export const GOOGLE_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
export const NAVER_CLIENT_ID = (process.env.REACT_APP_NAVER_CLIENT_ID || '').trim();

/**
 * Naver 콘솔에 등록한 콜백 URL. **문자 단위로 같아야** 한다 — 다르면 네이버가 로그인
 * 화면 대신 "잘못된 요청" 을 준다. 비워 두면 지금 주소를 쓴다(개발 서버처럼 오리진이
 * 오가는 환경용). 실서비스에서는 반드시 명시할 것 — 주소가 하나라도 달라지면 깨진다.
 */
const NAVER_CALLBACK_URL = (process.env.REACT_APP_NAVER_CALLBACK_URL || '').trim();

export const hasGoogle = () => !!GOOGLE_CLIENT_ID;
export const hasNaver = () => !!NAVER_CLIENT_ID;

const GIS_SRC = 'https://accounts.google.com/gsi/client';
let gisPromise = null;

/** GIS 스크립트를 한 번만 싣는다. 로그인 화면에서만 부르므로 앱 기동을 늦추지 않는다. */
function loadGis() {
    if (gisPromise) return gisPromise;
    gisPromise = new Promise((resolve, reject) => {
        if (typeof window === 'undefined') { reject(new Error('no window')); return; }
        if (window.google && window.google.accounts && window.google.accounts.oauth2) { resolve(); return; }
        const el = document.createElement('script');
        el.src = GIS_SRC;
        el.async = true;
        el.defer = true;
        el.onload = () => resolve();
        el.onerror = () => {
            // 다음 시도에서 다시 싣게 한다 — 한 번 실패했다고 영영 못 쓰게 두지 않는다
            gisPromise = null;
            reject(new Error('GOOGLE_SCRIPT_BLOCKED'));
        };
        document.head.appendChild(el);
    });
    return gisPromise;
}

/**
 * Google 팝업을 띄워 **access_token** 을 받는다.
 *
 * ID 토큰(credential)이 아니라 access_token 인 이유: QBase 의 `/qbase/api/google-login` 이
 * 그 토큰으로 Google 에 사용자 정보를 직접 물어본다(`fetchGoogleUserInfo`). 모양을 바꾸면
 * 서버가 못 읽는데 화면에는 "로그인 실패" 로만 보인다.
 *
 * `prompt: 'select_account'` — 계정이 여러 개인 사람이 늘 같은 계정으로 끌려가지 않게.
 */
export async function requestGoogleAccessToken() {
    if (!GOOGLE_CLIENT_ID) throw new Error('GOOGLE_NOT_CONFIGURED');
    await loadGis();
    return new Promise((resolve, reject) => {
        const client = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'openid email profile',
            prompt: 'select_account',
            callback: (res) => {
                if (res && res.access_token) resolve(res.access_token);
                else reject(new Error(res && res.error ? res.error : 'GOOGLE_NO_TOKEN'));
            },
            // 사용자가 팝업을 닫은 것은 **실패가 아니라 취소**다 — 붉은 오류를 띄우지 않는다
            error_callback: (err) => reject(Object.assign(
                new Error((err && err.type) || 'GOOGLE_POPUP_CLOSED'),
                { cancelled: true },
            )),
        });
        client.requestAccessToken();
    });
}

// ── Naver: 리다이렉트 방식 ────────────────────────────────────────────────
//
// 네이버는 팝업 토큰 발급이 없다. 지금 페이지를 떠났다가 `?code=…&state=…` 를 달고
// 돌아온다. 그래서 **떠나기 전에 state 를 남기고, 돌아와서 대조**한다.

const STATE_KEY = 'livesql_naver_oauth_state';

/** 이 창에서만 유효하면 된다. localStorage 에 두면 다른 탭의 흐름과 섞인다. */
function rememberState(state) {
    try { sessionStorage.setItem(STATE_KEY, state); } catch (_e) { /* 시크릿 모드 등 */ }
}
function takeState() {
    try {
        const v = sessionStorage.getItem(STATE_KEY);
        sessionStorage.removeItem(STATE_KEY);
        return v;
    } catch (_e) { return null; }
}

const randomState = () => {
    const buf = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(buf);
    return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
};

/** 네이버 동의 화면으로 **이 창을 보낸다.** 돌아오는 곳은 지금 주소다. */
export function startNaverLogin() {
    if (!NAVER_CLIENT_ID) throw new Error('NAVER_NOT_CONFIGURED');
    const redirectUri = NAVER_CALLBACK_URL || (window.location.origin + window.location.pathname);
    const state = randomState();
    rememberState(state);
    window.location.href = 'https://nid.naver.com/oauth2.0/authorize'
        + '?response_type=code'
        + `&client_id=${encodeURIComponent(NAVER_CLIENT_ID)}`
        + `&redirect_uri=${encodeURIComponent(redirectUri)}`
        + `&state=${encodeURIComponent(state)}`;
}

/**
 * 돌아온 주소에서 `code`·`state` 를 꺼낸다. 없으면 null(= 평범한 방문이다).
 *
 * **state 가 다르면 버린다.** 오픈데이터모델러는 저장된 state 가 없으면 검사를 건너뛰는데,
 * 그러면 남이 만든 code 를 우리 창에 먹일 수 있어(CSRF) state 를 둔 의미가 없어진다.
 * 새로고침으로 state 가 날아간 경우까지 실패로 보는 대신 **다시 로그인하면 되는** 쪽을 택한다.
 *
 * 꺼낸 뒤에는 주소에서 지운다 — 남겨 두면 새로고침할 때마다 소모된 code 로 다시 시도한다.
 */
export function readNaverCallback() {
    if (typeof window === 'undefined') return null;
    const sp = new URLSearchParams(window.location.search);
    const code = sp.get('code');
    const state = sp.get('state');
    const error = sp.get('error');
    const errorDesc = sp.get('error_description');
    if (!code && !error) return null;

    const saved = takeState();
    // 주소를 먼저 정리한다. 아래에서 무엇을 돌려주든 이 code 는 한 번만 쓰인다.
    // (지우기 전에 값은 위에서 이미 꺼내 뒀다 — 지운 뒤 읽으면 언제나 빈 값이다)
    try {
        sp.delete('code'); sp.delete('state'); sp.delete('error'); sp.delete('error_description');
        const qs = sp.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    } catch (_e) { /* 주소 정리 실패가 로그인을 막지는 않는다 */ }

    if (error) return { error: errorDesc || error };
    if (!saved || saved !== state) return { error: 'STATE_MISMATCH' };
    return { code, state };
}
